import type { LiveEvent } from '@setplex/tria-api'

import { api, EventStatus, LiveEventsTypes } from '@setplex/tria-api'
import { attach, combine, createEvent, createStore, sample } from 'effector'
import { not } from 'patronum'
import { register } from '~/core/repository'
import { model as liveEventsModel } from '~/entities/live-events'
import { formatLiveEventCardWithPrice } from '~/entities/live-events/events'
import { FORMAT_LIVE_EVENTS_TYPES } from '~/features/events/lib'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import { F, T, createUnsettledDetailsLoading } from '~/shared/helpers'
import { model as popupModel } from '~/widgets/Popups/InfoPopup'
import { getLiveEventBannerInfo } from './bannerInfo'
import { $eventsDetails } from './translations'

/** Matches the `RECOMMENDED_AMOUNT_VODS` / `RECOMMENDED_AMOUNT_SHOWS` ceiling. */
const RECOMMENDED_AMOUNT_LIVE_EVENTS = 12

type RecommendedLiveEventsParams = {
  eventId: number
  status?: EventStatus
  collectionId?: string
  page: number
  count: number
}

const TAB_NAME_BY_LISTING_STATUS: Record<EventStatus, string> = {
  [EventStatus.LIVE]: FORMAT_LIVE_EVENTS_TYPES[LiveEventsTypes.LIVE],
  [EventStatus.SOON]: FORMAT_LIVE_EVENTS_TYPES[LiveEventsTypes.UPCOMING],
  [EventStatus.LIVE_SOON]: FORMAT_LIVE_EVENTS_TYPES[LiveEventsTypes.UPCOMING],
  [EventStatus.ENDED]: FORMAT_LIVE_EVENTS_TYPES[LiveEventsTypes.PAST],
}

export const initExactEventPage = createEvent<{
  eventId: number
  query: URLSearchParams
}>()

export const left = createEvent()

export const $isOnThePage = createStore(false)
  .on(initExactEventPage, T)
  .on(left, F)

export const $isDetailsPageLoading = createUnsettledDetailsLoading({
  $isOnThePage,
  $entity: liveEventsModel.$currentLiveEvent,
  $pending: liveEventsModel.loadLiveEventFx.pending,
  settled: liveEventsModel.loadLiveEventFx.finally,
  failed: liveEventsModel.loadLiveEventFx.fail,
  resetOn: [initExactEventPage, left],
})

const shouldShowAlsoMayLikeSection = (query: URLSearchParams) =>
  query.get('fromSearch') !== 'true'

/**
 * Events have no vod-style `categoryId`. The listing used to open details
 * is passed on the URL: Events tabs as `status` and/or tab `categoryName`
 * (Live / Upcoming / Past); Home and dynamic rows as the row resource's own
 * `collectionId` or `status`. Search sets `fromSearch` and hides the grid
 * instead of showing a mixed listing.
 */
const listingStatusFromQuery = (query: URLSearchParams): EventStatus | null => {
  const status = query.get('status')
  if (status === EventStatus.LIVE || status === LiveEventsTypes.LIVE) {
    return EventStatus.LIVE
  }
  if (status === EventStatus.SOON || status === LiveEventsTypes.UPCOMING) {
    return EventStatus.SOON
  }
  if (status === EventStatus.ENDED || status === LiveEventsTypes.PAST) {
    return EventStatus.ENDED
  }
  if (status === EventStatus.LIVE_SOON) {
    return EventStatus.LIVE_SOON
  }

  const categoryName = query.get('categoryName')
  if (categoryName === FORMAT_LIVE_EVENTS_TYPES[LiveEventsTypes.LIVE]) {
    return EventStatus.LIVE
  }
  if (categoryName === FORMAT_LIVE_EVENTS_TYPES[LiveEventsTypes.UPCOMING]) {
    return EventStatus.SOON
  }
  if (categoryName === FORMAT_LIVE_EVENTS_TYPES[LiveEventsTypes.PAST]) {
    return EventStatus.ENDED
  }
  return null
}

export const $shouldShowAlsoMayLike = createStore(true).reset(left)

sample({
  clock: initExactEventPage,
  fn: ({ query }) => shouldShowAlsoMayLikeSection(query),
  target: $shouldShowAlsoMayLike,
})

const $detailsQuery = createStore(new URLSearchParams())
  .on(initExactEventPage, (_, { query }) => query)
  .reset(left)

// CONNECTIONS
sample({
  clock: initExactEventPage,
  filter: not(liveEventsModel.loadLiveEventFx.pending),
  target: liveEventsModel.loadLiveEventFx,
})

const $liveEventFormatted = combine(
  liveEventsModel.$currentLiveEvent,
  ssePurchasesModel.$liveEvent,
  (liveEvent, adjust) =>
    liveEvent ? formatLiveEventCardWithPrice(adjust(liveEvent)) : null
)

/**
 * "Also may like" for events. There is no vod-style category or
 * `recommended-to` endpoint for live events, so the recommendation list is
 * the listing used to open details, minus the event currently open: a
 * collection row via `collectionId`, or an Events tab / status row via
 * `status` and tab `categoryName`. A collection is its own listing and
 * mixes statuses, so a status must not narrow it. When neither listing is
 * on the URL, the open event's own status is the fallback. `LIVE_SOON` is
 * a combined-tab query value, not a status an event carries, so it maps
 * onto the Upcoming (`SOON`) listing when it comes from the event rather
 * than the query.
 *
 * Search-opened details do not show this grid (`fromSearch=true`).
 *
 * A dedicated `attach` (rather than calling the shared `getLiveEventsFx`
 * directly) keeps this page's `.doneData` from also firing whenever the
 * Events tabs, search, or library pages page through their own attaches of
 * the same base effect.
 */
const toRecommendedEventsListStatus = (status: EventStatus): EventStatus =>
  status === EventStatus.LIVE_SOON ? EventStatus.SOON : status

const loadRecommendedLiveEventsFx = attach({
  mapParams: ({ eventId: _eventId, ...params }: RecommendedLiveEventsParams) =>
    params,
  effect: api.liveEvents.getLiveEventsFx,
})

const $listingStatus = createStore<EventStatus | null>(null).reset(
  initExactEventPage,
  left
)

sample({
  clock: liveEventsModel.loadLiveEventFx.doneData,
  source: $detailsQuery,
  filter: (query) => shouldShowAlsoMayLikeSection(query),
  fn: (query, event) => {
    const collectionId = query.get('collectionId') || undefined

    return {
      eventId: event.id,
      ...(collectionId
        ? { collectionId }
        : {
            status:
              listingStatusFromQuery(query) ??
              toRecommendedEventsListStatus(event.status),
          }),
      page: 0,
      count: RECOMMENDED_AMOUNT_LIVE_EVENTS + 1, // +1 covers dropping the current event below
    }
  },
  target: loadRecommendedLiveEventsFx,
})

sample({
  clock: loadRecommendedLiveEventsFx,
  fn: ({ status }) => status ?? null,
  target: $listingStatus,
})

const $listingCollectionId = createStore('').reset(initExactEventPage, left)

sample({
  clock: loadRecommendedLiveEventsFx,
  fn: ({ collectionId }) => collectionId ?? '',
  target: $listingCollectionId,
})

/**
 * Analytics label for the listing details were opened from: the tab name for
 * a status listing, the row title the card already passed for a collection.
 */
const $openedFromTab = combine($listingStatus, $detailsQuery, (status, query) =>
  status
    ? (TAB_NAME_BY_LISTING_STATUS[status] ?? '')
    : (query.get('categoryName') ?? '')
)

const $listingStatusQuery = $listingStatus.map((status) => status ?? '')

const $recommendedLiveEventsRaw = createStore<LiveEvent[]>([])

sample({
  clock: loadRecommendedLiveEventsFx.done,
  source: liveEventsModel.$currentLiveEvent,
  filter: (current, { params }) => current?.id === params.eventId,
  fn: (_, { result }) => result.content || [],
  target: $recommendedLiveEventsRaw,
})

const $liveEventRecommended = combine(
  $recommendedLiveEventsRaw,
  $liveEventFormatted,
  ssePurchasesModel.$liveEvent,
  (events, current, adjust) => ({
    content: events
      .filter((event) => event.id !== current?.id)
      .slice(0, RECOMMENDED_AMOUNT_LIVE_EVENTS)
      .map((event) => formatLiveEventCardWithPrice(adjust(event))),
  })
)

/**
 * Fields the details `banner-description` shows beyond the plain event.
 * `countdownTo` is the Upcoming render gate. Live is a pill plus a bare time
 * range; Past drops the pill and prefixes the date onto the schedule line.
 * There is no "Available until" copy in the validated Events frames.
 */
const $liveEventBannerInfo = combine(
  $liveEventFormatted,
  $eventsDetails,
  (event, { liveTitle, upcomingTitle }) =>
    getLiveEventBannerInfo(event, liveTitle, upcomingTitle)
)

/******* reload event after countdown expires logic *******/
/**
 * Fired by the countdown when it reaches the start time. Refetching the event
 * is what switches the page to the Live now state — no page reload involved.
 */
export const reloadEventOnTime = createEvent<{
  eventId: number
}>()

sample({
  clock: reloadEventOnTime,
  source: {
    liveEvent: liveEventsModel.$currentLiveEvent,
    isPendingStatus: liveEventsModel.loadLiveEventFx.pending,
  },
  filter: ({ liveEvent, isPendingStatus }) =>
    liveEvent?.id != null && !isPendingStatus,
  fn: ({ liveEvent }) => ({ eventId: liveEvent?.id as number }), // as number covered by filter
  target: liveEventsModel.loadLiveEventFx,
})
/******* reload event after countdown expires logic *******/

// clear store
liveEventsModel.$currentLiveEvent.reset(initExactEventPage, left)
$recommendedLiveEventsRaw.reset(initExactEventPage, left)

sample({
  clock: initExactEventPage,
  target: popupModel.closeInfoPopup,
})

register(reloadEventOnTime, 'reloadEventOnTime')
register($liveEventFormatted, '$liveEventFormatted')
register(
  liveEventsModel.loadLiveEventFx.pending,
  '$routes/events/details/loading'
)
register($openedFromTab, '$openedFromTab')
register($listingStatusQuery, '$routes/events/details/listingStatus')
register($listingCollectionId, '$routes/events/details/listingCollectionId')
register($shouldShowAlsoMayLike, '$routes/events/details/shouldShowAlsoMayLike')
register($eventsDetails, '$eventsDetails')
register($liveEventBannerInfo, '$liveEventBannerInfo')
register($liveEventRecommended, '$liveEventRecommended')
